import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

/** Scroll depth (px) that collapses the header. */
export const HEADER_COLLAPSE_AT = 48
/** Scroll depth (px) under which the header expands again (hysteresis). */
export const HEADER_EXPAND_AT = 8
/**
 * Minimum vertical scroll range for a scroller to drive the header. It has to
 * outlast the height the collapse gives back, or collapsing would make the
 * content fit, clamp scrollTop to 0 and expand the header again in a loop.
 */
export const HEADER_COLLAPSE_MIN_RANGE = 160

/**
 * Opt-out for fixed-height views whose own scrollers are not page scroll
 * (the calendar's time grid jumps to 8:00 on load, which would collapse the
 * header before the reader has touched anything).
 */
export const SCROLL_COLLAPSE_IGNORE_ATTRIBUTE = 'data-scroll-collapse'

function isCollapseScroller(element: HTMLElement): boolean {
  if (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLInputElement ||
    element.isContentEditable ||
    element.closest(`[${SCROLL_COLLAPSE_IGNORE_ATTRIBUTE}="ignore"]`)
  ) {
    return false
  }
  return element.scrollHeight - element.clientHeight > HEADER_COLLAPSE_MIN_RANGE
}

interface ScrollCollapseOptions {
  /** Off: never collapses (and resets to expanded). */
  enabled?: boolean
  /** Changing it (typically the pathname) resets to expanded. */
  resetKey?: unknown
}

/**
 * True once the reader has scrolled into the content under `rootRef`, false
 * again back at the top — drives the one-line collapsed page header.
 *
 * Listens in the capture phase on the root so it works whichever descendant
 * owns the scroll: a page scroller, a flow table, a split-view list or a
 * nested panel. Portalled overlays (dialogs, popovers) are outside the root
 * and never count.
 */
export function useScrollCollapse(
  rootRef: RefObject<HTMLElement | null>,
  { enabled = true, resetKey }: ScrollCollapseOptions = {},
): boolean {
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Layouts are reconciled, not remounted, across route changes, so a
  // collapsed header would otherwise carry over to the next page. A page that
  // restores a scrolled position re-collapses through the listener.
  useEffect(() => {
    setIsCollapsed(false)
  }, [resetKey, enabled])

  useEffect(() => {
    const root = rootRef.current
    if (!root || !enabled) return

    // The scroller whose position collapsed the header; only it (or a wheel
    // gesture up once it has gone away) expands it again, so a second list
    // resting at its top cannot fight the one being read.
    let source: HTMLElement | null = null

    const onScroll = (event: Event) => {
      const target = event.target
      if (!(target instanceof HTMLElement) || !isCollapseScroller(target)) {
        return
      }
      const top = target.scrollTop
      if (top > HEADER_COLLAPSE_AT) {
        source = target
        setIsCollapsed(true)
      } else if (
        top < HEADER_EXPAND_AT &&
        (source === null || source === target)
      ) {
        source = null
        setIsCollapsed(false)
      }
    }

    // A view switch can unmount the scrolled list, leaving nothing at the top
    // to fire a scroll event; scrolling up is then the signal to expand.
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY >= 0 || !source) return
      if (!source.isConnected || source.scrollTop < HEADER_EXPAND_AT) {
        source = null
        setIsCollapsed(false)
      }
    }

    root.addEventListener('scroll', onScroll, { capture: true, passive: true })
    root.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      root.removeEventListener('scroll', onScroll, { capture: true })
      root.removeEventListener('wheel', onWheel)
    }
  }, [rootRef, enabled])

  return isCollapsed
}
